import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { propostaService, produtoServicoService, Proposta, ProdutoServico, PropostaItem } from '../../../services/propostaService';
import api from '../../../services/api';

interface Client {
  id: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj: string;
}

interface PropostaFormProps {
  proposta?: Proposta | null;
  onBack: () => void;
  onSaved: () => void;
  onAddClient?: () => void;
}

const condicoesPagtoOptions = [
  'À vista',
  '2x sem juros',
  '3x sem juros',
  'Cartão de crédito (até 12x)',
  'Boleto bancário (30 dias)',
  'Boleto bancário (30/60 dias)',
  'Personalizado',
];

export function PropostaForm({ proposta, onBack, onSaved, onAddClient }: PropostaFormProps) {
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [clientId, setClientId] = useState(proposta?.clientId || '');
  const [tipo, setTipo] = useState<'vendas' | 'servicos'>(proposta?.tipo || 'servicos');
  const [dataEmissao, setDataEmissao] = useState(proposta?.dataEmissao?.split('T')[0] || new Date().toISOString().split('T')[0]);
  const [dataValidade, setDataValidade] = useState(proposta?.dataValidade?.split('T')[0] || '');
  const [desconto, setDesconto] = useState(proposta?.desconto ? Number(proposta.desconto) : 0);
  const [observacoes, setObservacoes] = useState(proposta?.observacoes || '');
  const [condicoesPagto, setCondicoesPagto] = useState(proposta?.condicoesPagto || 'À vista');
  const [condicaoCustom, setCondicaoCustom] = useState('');
  
  // Items state
  const [itens, setItens] = useState<PropostaItem[]>(proposta?.itens || []);
  
  // Lookups
  const [clients, setClients] = useState<Client[]>([]);
  const [produtos, setProdutos] = useState<ProdutoServico[]>([]);

  useEffect(() => {
    loadClients();
    loadProdutos();
    // Set default validade (30 dias)
    if (!proposta) {
      const validade = new Date();
      validade.setDate(validade.getDate() + 30);
      setDataValidade(validade.toISOString().split('T')[0]);
    }
  }, []);

  const loadClients = async () => {
    try {
      const response = await api.get('/admin/clients');
      // A API retorna { data: [...], pagination: {...} }
      const clientsData = response.data?.data || response.data;
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    }
  };

  const loadProdutos = async () => {
    try {
      const data = await produtoServicoService.list({ active: true });
      setProdutos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setProdutos([]);
    }
  };



  const addItem = (produto?: ProdutoServico) => {
    const newItem: PropostaItem = {
      produtoServicoId: produto?.id,
      descricao: produto?.descricao || produto?.nome || '',
      quantidade: 1,
      valorUnit: produto?.valorUnit ? Number(produto.valorUnit) : 0,
      subtotal: produto?.valorUnit ? Number(produto.valorUnit) : 0,
    };
    setItens([...itens, newItem]);
  };

  const updateItem = (index: number, field: keyof PropostaItem, value: any) => {
    const updated = [...itens];
    updated[index] = { ...updated[index], [field]: value };
    // Recalcular subtotal
    if (field === 'quantidade' || field === 'valorUnit') {
      updated[index].subtotal = updated[index].quantidade * updated[index].valorUnit;
    }
    setItens(updated);
  };

  const removeItem = (index: number) => {
    setItens(itens.filter((_, i) => i !== index));
  };

  const selectProduto = (index: number, produtoId: number) => {
    const produto = produtos.find(p => p.id === produtoId);
    if (produto) {
      const updated = [...itens];
      updated[index] = {
        ...updated[index],
        produtoServicoId: produto.id,
        descricao: produto.descricao || produto.nome,
        valorUnit: Number(produto.valorUnit),
        subtotal: updated[index].quantidade * Number(produto.valorUnit),
      };
      setItens(updated);
    }
  };

  // Cálculos
  const subtotal = itens.reduce((acc, item) => acc + item.subtotal, 0);
  const valorDesconto = subtotal * (desconto / 100);
  const valorTotal = subtotal - valorDesconto;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || itens.length === 0) {
      alert('Selecione um cliente e adicione pelo menos um item');
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        clientId,
        tipo,
        dataEmissao,
        dataValidade,
        desconto,
        observacoes,
        condicoesPagto: condicoesPagto === 'Personalizado' ? condicaoCustom : condicoesPagto,
        itens: itens.map(item => ({
          produtoServicoId: item.produtoServicoId,
          descricao: item.descricao,
          quantidade: item.quantidade,
          valorUnit: item.valorUnit,
        })),
      };

      if (proposta) {
        await propostaService.update(proposta.id, data);
      } else {
        await propostaService.create(data);
      }
      onSaved();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar proposta');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <h2 className="text-xl font-bold">{proposta ? 'Editar Proposta' : 'Nova Proposta'}</h2>
        <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar
        </button>
      </div>

      {/* Cabeçalho da Proposta */}
      <div className="bg-white border rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 border-b pb-2">Dados da Proposta</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Cliente */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
            <div className="flex gap-2">
              <select
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="">Selecione um cliente</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.nomeFantasia || client.razaoSocial} - {client.cnpj}
                  </option>
                ))}
              </select>
              {onAddClient && (
                <button
                  type="button"
                  onClick={onAddClient}
                  className="px-3 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-1"
                  title="Cadastrar novo cliente"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
            {clients.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">Nenhum cliente cadastrado. Clique no + para adicionar.</p>
            )}
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <div className="flex rounded-lg border overflow-hidden">
              <button type="button" onClick={() => setTipo('servicos')}
                className={`flex-1 px-4 py-2 text-sm font-medium ${tipo === 'servicos' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Serviços
              </button>
              <button type="button" onClick={() => setTipo('vendas')}
                className={`flex-1 px-4 py-2 text-sm font-medium ${tipo === 'vendas' ? 'bg-cyan-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Produtos
              </button>
            </div>
          </div>

          {/* Datas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emissão</label>
            <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Validade</label>
            <input type="date" value={dataValidade} onChange={e => setDataValidade(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
          </div>
        </div>
      </div>


      {/* Itens da Proposta */}
      <div className="bg-white border rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="font-semibold text-gray-900">Itens da Proposta</h3>
          <button type="button" onClick={() => addItem()} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
            <Plus className="w-4 h-4" /> Item Avulso
          </button>
        </div>

        {/* Lista de itens */}
        <div className="space-y-3">
          {itens.map((item, index) => (
            <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
              {/* Descrição com select integrado */}
              <div className="flex-1">
                <div className="flex gap-2 mb-2">
                  <select
                    value={item.produtoServicoId || ''}
                    onChange={e => {
                      if (e.target.value) {
                        selectProduto(index, Number(e.target.value));
                      } else {
                        updateItem(index, 'produtoServicoId', undefined);
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white"
                  >
                    <option value="">Selecionar {tipo === 'servicos' ? 'serviço' : 'produto'}...</option>
                    {produtos
                      .filter(p => tipo === 'servicos' ? p.tipo === 'servico' : p.tipo === 'produto')
                      .map(p => (
                        <option key={p.id} value={p.id}>
                          {p.nome} - {formatCurrency(Number(p.valorUnit))}
                        </option>
                      ))}
                  </select>
                </div>
                <textarea
                  value={item.descricao}
                  onChange={e => updateItem(index, 'descricao', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white resize-none"
                  rows={2}
                  placeholder="Descrição detalhada do item..."
                />
              </div>

              {/* Quantidade */}
              <div className="w-20">
                <label className="block text-xs text-gray-500 mb-1">Qtd</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={item.quantidade}
                  onChange={e => updateItem(index, 'quantidade', parseInt(e.target.value) || 1)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center text-gray-900 bg-white"
                />
              </div>

              {/* Valor Unitário */}
              <div className="w-28">
                <label className="block text-xs text-gray-500 mb-1">Valor Unit.</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.valorUnit}
                  onChange={e => updateItem(index, 'valorUnit', parseFloat(e.target.value) || 0)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-right text-gray-900 bg-white"
                />
              </div>

              {/* Subtotal */}
              <div className="w-28 text-right">
                <label className="block text-xs text-gray-500 mb-1">Subtotal</label>
                <p className="py-2 font-semibold text-gray-900">{formatCurrency(item.subtotal)}</p>
              </div>

              {/* Remover */}
              <div className="pt-6">
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-2 text-red-500 hover:bg-red-100 rounded-lg"
                  title="Remover item"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Botão para adicionar mais itens */}
          {itens.length > 0 && (
            <button
              type="button"
              onClick={() => addItem()}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Adicionar outro item
            </button>
          )}

          {/* Estado vazio */}
          {itens.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">Nenhum item adicionado ainda.</p>
              <button
                type="button"
                onClick={() => addItem()}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Adicionar primeiro item
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Totais e Condições */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Condições */}
        <div className="bg-white border rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-gray-900 border-b pb-2">Condições Comerciais</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Condições de Pagamento</label>
            <select value={condicoesPagto} onChange={e => setCondicoesPagto(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white">
              {condicoesPagtoOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            {condicoesPagto === 'Personalizado' && (
              <textarea value={condicaoCustom} onChange={e => setCondicaoCustom(e.target.value)}
                placeholder="Descreva as condições de pagamento..." className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" rows={2} />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
              placeholder="Observações, prazos, garantias..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" rows={4} />
          </div>
        </div>

        {/* Totais */}
        <div className="bg-white border rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-gray-900 border-b pb-2">Resumo</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal ({itens.length} {itens.length === 1 ? 'item' : 'itens'})</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Desconto</span>
                <input type="number" min="0" max="100" step="1" value={desconto}
                  onChange={e => setDesconto(parseInt(e.target.value) || 0)}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center text-gray-900 bg-white" />
                <span className="text-gray-600">%</span>
              </div>
              <span className="text-red-600">- {formatCurrency(valorDesconto)}</span>
            </div>
            
            <div className="flex justify-between text-xl font-bold pt-3 border-t">
              <span>Total</span>
              <span className="text-cyan-600">{formatCurrency(valorTotal)}</span>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
